import { tt } from '@agos/shared'

import type { CartLine } from '@agos/customer-core'

/**
 * Putting a past order back in the basket.
 *
 * The rule that matters is **what cannot be reordered**, and legacy does not have one. It reconstructs a
 * product from the order line and adds it, so a dish that has since left the menu goes back in the
 * basket at the price it cost then — and the customer finds out at the checkout, or does not find out
 * at all.
 *
 * Here a line is reordered only if the product is still on the menu, and whatever is left behind is
 * **named**. FB-C02 is the same principle applied to a basket that has been sitting open; this is it
 * applied to one being refilled from history.
 *
 * A daily-menu line (negative id) is never reordered: Tuesday's menu is not on offer on Thursday, and
 * an order that carried one is a day the restaurant has moved past. Legacy reconstructs those too.
 */

export interface PastOrderItem {
  productId: number
  productName: string
  quantity: number
  boxGroupId?: string | null
  boxGroupName?: string | null
  boxSlotLabel?: string | null
}

export interface Reorder {
  lines: CartLine[]
  /** Names of what could not come back, for the customer to be told. */
  dropped: string[]
}

/**
 * The basket a past order becomes, against the catalogue as it is now.
 *
 * A box comes back as a box — its group id is regenerated so the new basket can remove it as one thing,
 * and a box that lost a member comes back with what is left rather than not at all: three quarters of a
 * burger box is still an order, and the customer can see what changed.
 */
export function reorderFrom(
  items: readonly PastOrderItem[],
  onMenu: ReadonlySet<number>,
): Reorder {
  const lines: CartLine[] = []
  const dropped: string[] = []
  // One new group id per old one, so two boxes in the same order stay two boxes.
  const groups = new Map<string, string>()

  for (const item of items) {
    if (item.productId <= 0) {
      // Assembled or daily-menu: there is no catalogue entry to check it against, and a lunch dish
      // belongs to a day that has passed.
      dropped.push(item.productName)
      continue
    }
    if (!onMenu.has(item.productId)) {
      dropped.push(item.productName)
      continue
    }
    const oldGroup = item.boxGroupId ?? null
    let group: string | undefined
    if (oldGroup !== null && oldGroup !== '') {
      group = groups.get(oldGroup)
      if (group === undefined) {
        group = `reorder:${oldGroup}:${groups.size}`
        groups.set(oldGroup, group)
      }
    }
    lines.push({
      productId: item.productId,
      extras: '',
      quantity: Math.max(1, item.quantity),
      ...(group === undefined
        ? {}
        : {
            boxGroupId: group,
            boxGroupName: item.boxGroupName ?? tt('Box'),
            boxSlotLabel: item.boxSlotLabel ?? '',
          }),
    })
  }

  return { lines, dropped: [...new Set(dropped)] }
}

/** What to tell the customer about a reorder that could not be complete. `''` when it could. */
export function reorderNote(reorder: Reorder): string {
  if (reorder.dropped.length === 0) return ''
  if (reorder.lines.length === 0) {
    return tt('Z tejto objednávky už nie je v ponuke nič.')
  }
  return `${tt('Toto už v ponuke nie je')}: ${reorder.dropped.join(', ')}`
}
